import React, { useContext, useEffect, useState } from "react";
import { Button, Grid, IconButton, List, ListItem, ListItemSecondaryAction, ListItemText, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import CustomerDocumentIcon from "@material-ui/icons/FolderSharedTwoTone";
import DownloadIcon from "@material-ui/icons/GetAppTwoTone";

import translate from "../i18n/Translator";
import Surface from "../components/Surface";
import Progress from "../components/Progress";
import { RouterParams } from "../router/RouterParams";
import { getCustomerDocument, approveCustomerDocument, refuseCustomerDocument, getCustomerDocumentUrl } from "../api/CustomerDocumentAPI";
import { AppContext } from "../context/AppContext";
import { CustomerDocuments } from "../model/CustomerDocument";
import { ErrorSnackbar } from "../components/Snackbars";
import DialogPopup from "../components/DialogPopup";

export default function CustomerDocument({ match }: RouterParams) {
    const context = useContext(AppContext);
    const theme = useTheme();
    const isXs = useMediaQuery(() => theme.breakpoints.down("xs"));

    const customerId = context.session?.customer?.id || match.params.customerId;
    const [status, setStatus] = useState<string>("loading");
    const [data, setData] = useState<CustomerDocuments>();
    const [error, setError] = useState<string>();
    const [dialog, setDialog] = useState<"approve" | "refuse">();
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setStatus("loading");
        getCustomerDocument(context.session!.tenant!.id, match.params.customerId).then(response => {
            setData(response);
            setStatus("loaded");
        }).catch(error => {
            setStatus(error.message);
        });
        // eslint-disable-next-line
    }, [match.params.customerId]);

    const onDownloadDocument = (documentId: string) => {
        getCustomerDocumentUrl(context.session!.tenant!.id, customerId, documentId).then(url => {
            window.open(url, "_blank");
        }).catch((error) => {
            setError(error.message);
        });
    };

    const onApprove = () => {
        setDialog("approve");
    };

    const onApproved = () => {
        setSubmitting(true);
        approveCustomerDocument(context.session!.tenant!.id, match.params.customerId).then(response => {
            onClose();
            setData(response);
        }).catch(error => {
            setError(error.message);
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const onRefuse = () => {
        setDialog("refuse");
    };

    const onRefused = () => {
        setSubmitting(true);
        refuseCustomerDocument(context.session!.tenant!.id, match.params.customerId).then(response => {
            onClose();
            setData(response);
        }).catch(error => {
            setError(error.message);
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const onClose = () => {
        setDialog(undefined);
    };

    if (status === "loading" || !data) {
        return <Progress />;
    }

    if (status !== "loaded") {
        return (
            <Typography variant="body1" component="h5" color="error" align="center">
                {status}
            </Typography>
        );
    }

    return (
        <Surface title={data.customer.name} subtitle={translate(`documents.status.${data.status}`)} icon={<CustomerDocumentIcon />} backButton>
            <Grid container justify="center">
                <Grid item xs={12} lg={8} xl={6}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <List>
                                {data.documents.map(item => (
                                    <ListItem key={item.id}>
                                        <ListItemText primary={item.name} secondary={item.description} />
                                        <ListItemSecondaryAction>
                                            <IconButton edge="end" onClick={() => onDownloadDocument(item.id)} disabled={!item.value}>
                                                <DownloadIcon color={item.value ? "primary" : "disabled"} />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                                {data.documents.length === 0 && (
                                    <Typography variant="body1" component="h5" align="center">
                                        {translate("documents.empty")}
                                    </Typography>
                                )}
                            </List>
                        </Grid>
                        {context.isGranted("DocumentsUpdate") && data.status === "VALIDATING" && (
                            <Grid item xs={12}>
                                <Grid container justify="flex-end" spacing={1}>
                                    <Grid item xs={12} sm="auto">
                                        <Button variant="outlined" color="secondary" size="large" fullWidth={isXs} onClick={onRefuse}>
                                            {translate("buttons.refuse")}
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm="auto">
                                        <Button variant="contained" color="primary" size="large" fullWidth={isXs} onClick={onApprove}>
                                            {translate("buttons.approve")}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
            {dialog && (
                <DialogPopup open title={translate(`documents.${dialog}.title`)} onClose={onClose}
                    disable={submitting}
                    disableBackdropClick={submitting}
                    disableEscapeKeyDown={submitting}
                    closeColor="default"
                    closeText={translate("buttons.cancel") as string}
                    button={(
                        <Button onClick={dialog === "approve" ? onApproved : onRefused} variant="outlined" color={dialog === "approve" ? "primary" : "secondary"} disabled={submitting}>
                            {translate("buttons.continue")}
                        </Button>
                    )}>
                    <Grid container justify="space-between" alignItems="center">
                        <Grid item xs={12}>
                            {translate(`documents.${dialog}.text`, { "customer": data.customer.name })}
                        </Grid>
                    </Grid>
                    <ErrorSnackbar message={error} onClose={() => setError(undefined)} />
                </DialogPopup>
            )}
            <ErrorSnackbar message={error} onClose={() => setError(undefined)} />
        </Surface>
    )
}